import React, {Fragment, FunctionComponent, useEffect, useState} from "react";
import {PageTemplate} from "../../common/page-template/PageTemplate";
import {useTranslation} from "react-i18next";
import {ActionButton} from "../../common/page-template/ActionButton";
import AddIcon from '@mui/icons-material/Add';
import {
    MetadataCategoryData,
    MetadataData,
    useGetApiV1UserMeQuery,
    usePostApiV1MetadataCategoryMutation,
    usePutApiV1MetadataMutation
} from "../../../api/GovernanceToolApi";
import {ErrorMessage} from "../../common/error-message/ErrorMessage";
import {Loader} from "../../common/loader/Loader";
import {UserRole, UserRoleUtils} from "../../../book/UserRoleUtils";
import {AppRoute} from "../../../book/AppRoute";
import {useNavigate} from "react-router-dom";
import {Tab, Tabs} from "@mui/material";
import {CategoryMetadataManagement} from "./CategoryMetadataManagement";
import {
    ProcessStatusNotification,
    toProcessStatus
} from "../../common/process-status-notification/ProcessStatusNotification";
import {EditMetadataDialog} from "./EditMetadataDialog";

export const MetadataManagement: FunctionComponent = () => {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const [state, setState] = useState<MetadataManagementState>({});
    const {data: userMe} = useGetApiV1UserMeQuery(undefined);
    const [searchMetadataCategories, {
        data: metadataCategoriesData,
        isLoading: isMetadataCategoriesLoading,
        error: metadataCategoriesError,
    }] = usePostApiV1MetadataCategoryMutation();
    const [editMetadata, {
        data: editMetadataData,
        isLoading: isEditMetadataLoading,
        error: editMetadataError,
        reset: resetEditMetadata
    }] = usePutApiV1MetadataMutation();

    const role = userMe?.payload?.role;
    const metadataCategories = metadataCategoriesData?.payload?.metadataCategories ?? [];

    useEffect(() => {
        if (role) {
            if (UserRoleUtils.ToRole(role) < UserRole.SuperUser) {
                navigate(AppRoute.ProductsSearch);
            } else {
                searchMetadataCategories({searchMetadataCategoriesRequest: {}}).then(result => {
                    if ('data' in result && result && result.data && result.data.payload) {
                        setState(state => ({...state, currentCategory: result.data.payload!.metadataCategories![0]}))
                    }
                });
            }
        }
    }, [role]);

    return <PageTemplate pageTitle={t("Metadata management")}
                         actions={[
                             <ActionButton key={`action-button-add-metadata`}
                                           icon={<AddIcon/>}
                                           onClick={() => {
                                               setState(state => ({...state, metadataToEdit: {} as MetadataData}))
                                           }}
                                           label={t("Add metadata")}/>
                         ]}>

        {isMetadataCategoriesLoading && <Loader message={t("Loading metadata…")}/>}

        {Boolean(metadataCategoriesError) && <ErrorMessage
            message={t("An error occurred while loading users, please contact the administrator or try again")}/>}


        {Boolean(metadataCategories.length) && <Fragment>
            <Tabs value={state.currentCategory?.code || false}
                  onChange={(e, categoryCode: string) => {
                      setState({...state, currentCategory: {code: categoryCode}});
                  }}
                  variant="scrollable"
                  scrollButtons="auto"
                  aria-label={t("Metadata categories").toLowerCase()}>

                {metadataCategories.map(category =>
                    <Tab label={category.code}
                         key={`tab-${category.code}`}
                         value={category.code}/>)}
            </Tabs>

            {state.currentCategory &&
                <CategoryMetadataManagement category={state.currentCategory}
                                            categories={metadataCategories}/>}
        </Fragment>}

        {state.metadataToEdit &&
            <EditMetadataDialog metadata={state.metadataToEdit}
                                metadataCategories={metadataCategories}
                                onCancelClick={() => {
                                    setState(state => ({
                                        ...state,
                                        metadataToEdit: undefined
                                    }));
                                }}
                                onOkClick={(category, value) => {
                                    if (state.metadataToEdit) {
                                        editMetadata({
                                            updateMetadataRequest: {
                                                metadataId: state.metadataToEdit.id,
                                                category,
                                                value
                                            }
                                        }).then(() => {
                                            searchMetadataCategories({searchMetadataCategoriesRequest: {}});

                                            resetEditMetadata();
                                        })
                                        setState(state => ({
                                            ...state,
                                            metadataToEdit: undefined
                                        }));
                                    }
                                }}/>}

        <ProcessStatusNotification
            status={toProcessStatus(isEditMetadataLoading, Boolean(editMetadataData?.payload), Boolean(editMetadataError))}
            onClose={() => resetEditMetadata()}
            onLoadingMessage={t("Adding metadata please wait…")}
            onSuccessMessage={t("Metadata added successfully")}
            onErrorMessage={t("Metadata adding failed")}/>
    </PageTemplate>
}

interface MetadataManagementState {
    currentCategory?: MetadataCategoryData;
    metadataToEdit?: MetadataData;
}